import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { IssueType, Severity } from '../../graphql/enums';
import { useFetcher } from '../../graphql/hooks/useFetcher';

// ---------------------------------------------------------------------------
// CalendarEventData — the plain-object shape the calendar grids consume.
//
// This mirrors `CalendarEvent` from `@gripp/shared-ui/Calendar/calendar.types`
// exactly, but is defined here to avoid a shared-logic → shared-ui import
// cycle. Phase 2 S2 (the screen wiring) may cast the items to `CalendarEvent`
// directly — the shapes are identical.
// ---------------------------------------------------------------------------
export interface CalendarEventData {
  /** Issue id — clicking the pill navigates to `/activity/:id`. */
  id: string;
  /** Effective placement date — `effectiveCreationDate ?? createdAt`, as luxon. */
  at: DateTime;
  /** Pill background — the activity's type color (`typeColors[variant]`). */
  color: string;
  /** Primary pill label (issue title). */
  title: string;
  /** Optional second line (linked asset name) when `showAssetInPill`. */
  asset?: string;
  /** Hour-of-day (0–23) for week-grid row placement. Derived from `at`. */
  hour: number;
}

// ---------------------------------------------------------------------------
// Color palette — copied from shared-ui's typeColors so we can resolve the
// color here without importing shared-ui.
// ---------------------------------------------------------------------------
const TYPE_COLORS: Record<string, string> = {
  attention: '#E24D2A',
  urgent: '#E24D2A',
  blocker: '#F9C431',
  pending: '#F9C431',
  done: '#36B37E',
  solved: '#36B37E',
  routines: '#4A90E2',
  note: '#7E4CC9',
  campaign: '#D649A6',
  notification: '#FF5505',
};

// Fallback when a severity doesn't map to a known variant.
const FALLBACK_COLOR = '#4A90E2';

// Severity → TypeVariant (mirrors severityToVariant in shared-ui).
const SEVERITY_TO_VARIANT: Record<Severity, string> = {
  [Severity.Red]: 'attention',
  [Severity.Yellow]: 'blocker',
  [Severity.Green]: 'done',
  [Severity.Purple]: 'note',
  [Severity.Blue]: 'routines',
};

// Issue-type overrides: campaign issues use the 'campaign' pink regardless of
// severity (mirrors the activity feed). `issue.type` is an `IssueType`, so we key
// on `IssueType.Campaign` ('CAMPAIGN') — NOT `ActivityType.CampaignCreated`
// ('CAMPAIGN_CREATED'), which never matches a findIssues `type` value.
const ISSUE_TYPE_VARIANT_OVERRIDES: Partial<Record<IssueType, string>> = {
  [IssueType.Campaign]: 'campaign',
};

// ---------------------------------------------------------------------------
// activityToCalendarEvent — pure mapping function (one place, easy to test).
// ---------------------------------------------------------------------------

/** Raw issue shape returned by the FIND_ISSUES_CALENDAR query. */
export interface CalendarIssue {
  id: string;
  title: string;
  severity: Severity;
  type: IssueType;
  effectiveCreationDate: string | null;
  createdAt: string;
  asset?: {
    name: string;
  } | null;
}

/**
 * Maps a raw issue from findIssues onto a CalendarEventData, ready for the
 * calendar grids.
 *
 * - `at` / `hour`: derived from `effectiveCreationDate ?? createdAt` (luxon).
 * - `color`: `typeColors[activityTypeOverride ?? severityToVariant[severity]]`.
 *   CampaignCreated issues always get the campaign pink regardless of severity.
 * - `asset`: the asset's name if the issue has a linked asset.
 */
export function activityToCalendarEvent(issue: CalendarIssue): CalendarEventData {
  const dateStr = issue.effectiveCreationDate ?? issue.createdAt;
  const at: DateTime = DateTime.fromISO(dateStr);

  const typeVariant =
    ISSUE_TYPE_VARIANT_OVERRIDES[issue.type] ??
    SEVERITY_TO_VARIANT[issue.severity];

  const color = TYPE_COLORS[typeVariant] ?? FALLBACK_COLOR;

  return {
    id: issue.id,
    at,
    color,
    title: issue.title,
    asset: issue.asset?.name ?? undefined,
    hour: at.hour,
  };
}

// ---------------------------------------------------------------------------
// useActivityCalendar hook
// ---------------------------------------------------------------------------

/**
 * Window (ISO string or luxon DateTime) passed to useActivityCalendar.
 * Using ISO strings avoids luxon import ceremony at call sites, but luxon
 * DateTime is accepted too (toISO() is called on .start / .end if present).
 */
export interface CalendarWindow {
  /** Start of the visible range (inclusive). ISO string or luxon DateTime. */
  start: DateTime | string;
  /** End of the visible range (inclusive). ISO string or luxon DateTime. */
  end: DateTime | string;
}

/**
 * Raw filter that mirrors the `FindIssuesFilter` GraphQL input. Callers
 * pass any subset of the filter the activity-list screen already uses;
 * `startDate` and `endDate` are injected from `window` and must not be
 * included in `filter` (they would be overwritten).
 */
export interface CalendarFilter {
  severity?: Severity[];
  workspace?: { eq?: string };
  groupIds?: string[];
  includeUngrouped?: boolean;
  labelIds?: string[];
  includeUnlabeled?: boolean;
  keyword?: string;
  excludeActivityTypes?: IssueType[];
  [key: string]: unknown;
}

function toISO(value: DateTime | string): string {
  if (typeof value === 'string') return value;
  // DateTime is typed as `any` via the ambient shim in src/types/luxon.d.ts —
  // the cast is intentional and consistent with how the shim is documented.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (value as any).toISO() as string;
}

/**
 * Fetches all issues in a calendar window and maps them to `CalendarEventData`.
 *
 * - Wraps `findIssues` with `startDate`/`endDate` scoped to `window` so only
 *   issues that fall in the visible month/week are returned.
 * - NO pagination is sent — but the server caps the result at
 *   `MAX_UNBOUNDED_ITEMS` (findIssues.ts) so the response can never exceed
 *   MongoDB's 16MB BSON limit (the bug that crashed the cross-workspace
 *   _Gripp view and threatens large single workspaces — plan 0017 §6.5).
 *   The query asks for `count` (the true total before the cap); when it
 *   exceeds the returned `events`, `isTruncated` is `true` so the caller can
 *   tell the user to narrow their filters. This is NOT a silent cap.
 * - Query key encodes the full filter + window so React Query invalidates
 *   correctly when either changes (Phase 3 will invalidate via the `issue` key).
 *
 * @param filter - Activity filter (severity, groups, labels, keyword, etc.).
 *   Should NOT include `startDate` / `endDate` — those come from `window`.
 * @param window - Visible calendar range `{ start, end }` (luxon or ISO).
 * @param options - Optional react-query passthrough. `enabled` (default `true`)
 *   lets a caller skip the fetch while the calendar isn't shown or the workspace
 *   id hasn't resolved yet.
 */
export function useActivityCalendar(
  filter: CalendarFilter,
  window: CalendarWindow,
  options?: { enabled?: boolean }
) {
  const startISO = toISO(window.start);
  const endISO = toISO(window.end);

  const variables = {
    filter: {
      ...filter,
      startDate: startISO,
      endDate: endISO,
    },
  };

  const cacheKey = ['issue', 'calendar', startISO, endISO, filter] as const;

  const fetcherFn = useFetcher<
    { issue: { findIssues: { count: number; items: CalendarIssue[] } } },
    typeof variables
  >(FIND_ISSUES_CALENDAR);

  const result = useQuery({
    queryKey: cacheKey,
    queryFn: () => fetcherFn(variables),
    enabled: options?.enabled ?? true,
    select: (data) => {
      const findIssues = data?.issue?.findIssues;
      const items = findIssues?.items ?? [];
      const events = items.map(activityToCalendarEvent);
      return {
        events,
        // `count` is the true total matched server-side; `events.length` is the
        // (possibly capped) number actually returned. When count > events the
        // server truncated the window.
        totalCount: findIssues?.count ?? events.length,
      };
    },
  });

  const events = result.data?.events ?? [];
  const totalCount = result.data?.totalCount ?? events.length;

  return {
    ...result,
    /** Mapped calendar events, ready for the calendar grids. */
    events,
    /** True total matched in the window, before the server cap. */
    totalCount,
    /** True when the server capped the window (more activities than shown). */
    isTruncated: totalCount > events.length,
  };
}

// ---------------------------------------------------------------------------
// GraphQL query — mirrors the fields already used in activityList.tsx plus
// the fields activityToCalendarEvent needs. No pagination variables since the
// hook fetches the whole visible window at once.
// ---------------------------------------------------------------------------
const FIND_ISSUES_CALENDAR = `
  query FindIssuesCalendar($filter: FindIssuesFilter) {
    issue {
      findIssues(filter: $filter) {
        count
        items {
          id
          title
          severity
          type
          effectiveCreationDate
          createdAt
          asset {
            name
          }
        }
      }
    }
  }
`;
